/**
 * Stable Playwright / console probe for GlobalVars.RTData.Views.
 * Replaces Vue 2 DOM scrape: el.__vue__.GlobalVars.RTData.Views + __ob__.dep.id.
 * All public getters return plain JSON (no Vue proxies).
 */
import { toRaw } from 'vue';
import utils from './utils.jsx';

function snapshotValue(value) {
    try {
        const raw = value != null && typeof value === 'object' ? toRaw(value) : value;
        return JSON.parse(JSON.stringify(raw));
    } catch (e) {
        return { serializeError: String(e) };
    }
}

function rankOf(key, value, viewUpdateSeq) {
    if (viewUpdateSeq && typeof viewUpdateSeq[key] === 'number')
        return viewUpdateSeq[key];
    const depId = value?.__ob__?.dep?.id;
    if (typeof depId === 'number')
        return depId;
    return -1;
}

function liveRTData() {
    return utils.global_variables?.RTData || null;
}

/** Plain snapshot of RTData fields useful for tests (no methods / proxies). */
function getRTData() {
    const rt = liveRTData();
    if (!rt)
        return null;

    return {
        Views: snapshotValue(rt.Views || {}),
        ViewUpdateSeq: snapshotValue(rt.ViewUpdateSeq || {}),
        IsActive: !!rt.IsActive,
        Status: rt.Status ?? '',
    };
}

/** Plain JSON copy of Views. */
function getRTDataViews() {
    const rt = liveRTData();
    if (!rt?.Views)
        return null;
    return snapshotValue(rt.Views);
}

function collectPrefixMatches(prefix) {
    const rt = liveRTData();
    const views = rt?.Views;
    if (!views)
        return null;

    const viewUpdateSeq = rt.ViewUpdateSeq || null;
    const matches = [];

    for (const [key, value] of Object.entries(views)) {
        if (!key.startsWith(prefix))
            continue;
        matches.push({
            key,
            updateSeq: rankOf(key, value, viewUpdateSeq),
            value: snapshotValue(value),
        });
    }

    matches.sort((a, b) => b.updateSeq - a.updateSeq);
    return matches;
}

/**
 * All Views whose key starts with `prefix`, as plain JSON.
 * Sorted by updateSeq descending (most recently written first).
 *
 * @param {string} prefix
 * @param {{ timeout?: number, waitForMatch?: boolean }} [options]
 * @returns {Promise<Array<{ key: string, updateSeq: number, value: unknown }>>}
 */
function getLatestRTDataView(prefix, { timeout = 30000, waitForMatch = true } = {}) {
    return new Promise((resolve, reject) => {
        const start = Date.now();

        const poll = () => {
            const matches = collectPrefixMatches(prefix);

            if (matches === null) {
                if (Date.now() - start > timeout) {
                    reject(new Error('RTData.Views not available within timeout'));
                    return;
                }
                requestAnimationFrame(poll);
                return;
            }

            if (matches.length === 0) {
                if (!waitForMatch || Date.now() - start > timeout) {
                    resolve([]);
                    return;
                }
                requestAnimationFrame(poll);
                return;
            }

            resolve(matches);
        };

        poll();
    });
}

export function installCareTestProbe() {
    if (typeof window === 'undefined')
        return;

    window.careTest = {
        getRTData,
        getRTDataViews,
        getLatestRTDataView,
    };
}

export default { installCareTestProbe, getRTData, getRTDataViews, getLatestRTDataView };
